import { Model } from 'app/model/backendmodel';
import { LakehouseTableCopySink } from 'app/model/pipeline/copysink/lakehouse-table.copysink';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { AdapterRegistry, Metamodel, Owned, StoreSettingPartitionKey } from 'shell-models-lib';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';
import { GlobalVariables } from 'app/services/globals';
import { isDeltaCloneCopy } from 'app/authoring/copy-job/wizard/copy-job-delta.util';
import type { CopyActivity } from 'app/model/pipeline/copy.activity';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(LakehouseTableCopySink),
    asynchronous: true,
    adapts: {
        type: '*',
        partitionOption: '*',
        partitionNameList: '*',
        tableActionOption: '*',
        upsertSettings: __IS_FABRIC_DPE__
            ? {
                  keys: '*'
              }
            : undefined,
        applyVOrder: __IS_FABRIC_DPE__ ? '*' : undefined,
        maxRowsPerFile: __IS_FABRIC_DPE__ ? '*' : undefined,
        enableTimestampNtz: __IS_FABRIC_DPE__ && GlobalVariables.timestampNtz ? '*' : undefined,
        enableChangeDataFeed: () => __IS_FABRIC_DPE__ && GlobalVariables.cdfAutoCreate ? '*' : undefined
    }
})
export class LakehouseTableCopySinkAdapter extends CopySinkAdapter<LakehouseTableCopySink, Model.LakehouseTableSink> {
    protected override async _toResource(model: LakehouseTableCopySink): Promise<Model.LakehouseTableSink> {
        const result: Model.LakehouseTableSink = {
            type: adfModelTypeMap.lakehouseTable.copySinkType
        };
        await this._lhTablePropsToResource(result, model);
        return result;
    }

    protected override async _toModel(
        resource: Model.LakehouseTableSink,
        copySink: CopySink
    ): Promise<LakehouseTableCopySink> {
        const result = Owned.create<LakehouseTableCopySink>(Metamodel.modelFor(LakehouseTableCopySink), copySink);
        this._lhTablePropsToModel(resource, result, copySink);
        return result;
    }

    private async _lhTablePropsToResource(
        result: Model.LakehouseTableSink,
        model: LakehouseTableCopySink
    ): Promise<void> {
        result.tableActionOption = ValueAdapter.toResource(model, LakehouseTableCopySink.TABLE_ACTION) || 'Append';
        if (result.tableActionOption === 'Upsert') {
            await this._lhTableActionUpsertToResource(result, model);
        } else if (result.tableActionOption === 'SCD2') {
            await this._lhTableActionUpsertSettingsToResource(result, model);
        } else if (result.tableActionOption === 'Append') {
            this._lhTableActionAppendToResource(result, model);
        } else if (result.tableActionOption === 'Overwrite' || result.tableActionOption === 'OverwriteSchema') {
            this._lhTableActionOverwriteToResource(result, model);
        } else if (ValueAdapter.isTemplated(model, LakehouseTableCopySink.TABLE_ACTION)) {
            await this._lhTableActionUpsertSettingsToResource(result, model);
            this._lhTableActionOverwriteToResource(result, model);
        }

        if (__IS_FABRIC_DPE__) {
            result.applyVOrder = ValueAdapter.toResource(model, LakehouseTableCopySink.APPLY_VORDER);
            result.maxRowsPerFile = ValueAdapter.toResource(model, LakehouseTableCopySink.MAX_ROWS_PER_FILE);
            if (GlobalVariables.timestampNtz) {
                result.enableTimestampNtz = ValueAdapter.toResource(model, LakehouseTableCopySink.ENABLE_TIMESTAMP_NTZ);
            }
            if (GlobalVariables.cdfAutoCreate && model.enableChangeDataFeed != null) {
                result.enableChangeDataFeed = ValueAdapter.toResource(model, LakehouseTableCopySink.ENABLE_CHANGE_DATA_FEED);
            }
        }
    }

    private _lhTablePropsToModel(
        resource: Model.LakehouseTableSink,
        result: LakehouseTableCopySink,
        copySink: CopySink
    ): void {
        ValueAdapter.toModel(result, LakehouseTableCopySink.TABLE_ACTION, resource.tableActionOption);
        if (result.tableActionOption === 'Upsert') {
            this._lhTableActionUpsertToModel(resource, result);
        } else if (result.tableActionOption === 'SCD2') {
            this._lhTableActionUpsertSettingsToModel(resource, result);
        } else if (resource.tableActionOption === 'Append') {
            this._lhTableActionAppendToModel(resource, result);
        } else if (resource.tableActionOption === 'Overwrite' || resource.tableActionOption === 'OverwriteSchema') {
            result.isOldResource = resource.tableActionOption === 'Overwrite';
            this._lhTableActionOverwriteToModel(resource, result);
        } else if (isExpressionObject(resource.tableActionOption)) {
            this._lhTableActionUpsertToModel(resource, result);
            this._lhTableActionOverwriteToModel(resource, result);
            this._lhTableActionAppendToModel(resource, result);
        }

        if (__IS_FABRIC_DPE__) {
            if (resource.applyVOrder == null) {
                result.applyVOrder = undefined;
            }
            ValueAdapter.toModel(result, LakehouseTableCopySink.APPLY_VORDER, resource.applyVOrder);
            ValueAdapter.toModel(result, LakehouseTableCopySink.MAX_ROWS_PER_FILE, resource.maxRowsPerFile);

            if (GlobalVariables.timestampNtz) {
                ValueAdapter.toModel(result, LakehouseTableCopySink.ENABLE_TIMESTAMP_NTZ, resource.enableTimestampNtz);
            }
            if (GlobalVariables.cdfAutoCreate) {
                ValueAdapter.toModel(result, LakehouseTableCopySink.ENABLE_CHANGE_DATA_FEED, resource.enableChangeDataFeed);
            }
        }
    }

    private async _lhTableActionUpsertSettingsToResource(
        result: Model.LakehouseTableSink,
        model: LakehouseTableCopySink
    ): Promise<void> {
        if (__IS_FABRIC_DPE__ && !isDeltaCloneCopy(model.owner as CopyActivity)) {
            result.upsertSettings = {
                keys: ValueAdapter.toResource(model, LakehouseTableCopySink.KEY_COLUMNS)
            };
        }
    }

    private async _lhTableActionUpsertToResource(
        result: Model.LakehouseTableSink,
        model: LakehouseTableCopySink
    ): Promise<void> {
        await this._lhTableActionUpsertSettingsToResource(result, model);

        if (__IS_FABRIC_DPE__ && GlobalVariables.lakehouseUpsertPartition) {
            if (ValueAdapter.isTemplated(model, LakehouseTableCopySink.UPSERT_PARTITION_OPTION)) {
                result.partitionOption = ValueAdapter.toResource(model, LakehouseTableCopySink.UPSERT_PARTITION_OPTION);
            } else {
                result.partitionOption =
                    model.enableUpsertPartitionOption === true
                        ? 'PartitionByKey'
                        : model.enableUpsertPartitionOption === false
                        ? 'None'
                        : undefined;
            }
            if (result.partitionOption && result.partitionOption !== 'None') {
                if (ValueAdapter.isTemplated(model, LakehouseTableCopySink.UPSERT_PARTITION_KEYS)) {
                    result.partitionNameList = ValueAdapter.toResource(model, LakehouseTableCopySink.UPSERT_PARTITION_KEYS);
                } else if (!arrayIsNullOrEmpty(model.upsertPartitionNameList)) {
                    result.partitionNameList = [];
                    model.upsertPartitionNameList.forEach((partitionName) => {
                        if (partitionName.partitionKey) {
                            (result.partitionNameList as string[]).push(partitionName.partitionKey);
                        }
                    });
                }
            }
        }
    }

    private _lhTableActionAppendToResource(result: Model.LakehouseTableSink, model: LakehouseTableCopySink): void {
        if (ValueAdapter.isTemplated(model, LakehouseTableCopySink.APPEND_PARTITION_OPTION)) {
            result.partitionOption = ValueAdapter.toResource(model, LakehouseTableCopySink.APPEND_PARTITION_OPTION);
        } else {
            result.partitionOption =
                model.enableAppendPartitionOption === true
                    ? 'PartitionByKey'
                    : model.enableAppendPartitionOption === false
                      ? 'None'
                      : undefined;
        }
        if (result.partitionOption && result.partitionOption !== 'None') {
            if (ValueAdapter.isTemplated(model, LakehouseTableCopySink.APPEND_PARTITION_KEYS)) {
                result.partitionNameList = ValueAdapter.toResource(model, LakehouseTableCopySink.APPEND_PARTITION_KEYS);
            } else if (!arrayIsNullOrEmpty(model.appendPartitionNameList)) {
                result.partitionNameList = [];
                model.appendPartitionNameList.forEach((partitionName) => {
                    if (partitionName.partitionKey) {
                        (result.partitionNameList as string[]).push(partitionName.partitionKey);
                    }
                });
            }
        }
    }

    private _lhTableActionOverwriteToResource(result: Model.LakehouseTableSink, model: LakehouseTableCopySink): void {
        if (ValueAdapter.isTemplated(model, LakehouseTableCopySink.PARTITION_OPTION)) {
            result.partitionOption = ValueAdapter.toResource(model, LakehouseTableCopySink.PARTITION_OPTION);
        } else {
            result.partitionOption = model.enablePartitionOption ? 'PartitionByKey' : 'None';
        }
        if (result.partitionOption !== 'None') {
            if (ValueAdapter.isTemplated(model, LakehouseTableCopySink.PARTITION_KEYS)) {
                result.partitionNameList = ValueAdapter.toResource(model, LakehouseTableCopySink.PARTITION_KEYS);
            } else if (!arrayIsNullOrEmpty(model.partitionNameList)) {
                result.partitionNameList = [];
                model.partitionNameList.forEach((partitionName) => {
                    if (partitionName.partitionKey) {
                        (result.partitionNameList as string[]).push(partitionName.partitionKey);
                    }
                });
            }
        }
    }

    private _lhTableActionUpsertSettingsToModel(resource: Model.LakehouseTableSink, result: LakehouseTableCopySink): void {
        if (resource.upsertSettings) {
            ValueAdapter.toModel(result, LakehouseTableCopySink.KEY_COLUMNS, resource.upsertSettings.keys);
        }
    }

    private _lhTableActionUpsertToModel(resource: Model.LakehouseTableSink, result: LakehouseTableCopySink): void {
        this._lhTableActionUpsertSettingsToModel(resource, result);

        if (__IS_FABRIC_DPE__ && GlobalVariables.lakehouseUpsertPartition) {
            if (resource.partitionOption == null) {
                result.enableUpsertPartitionOption = null;
            } else if (resource.partitionOption === 'None') {
                result.enableUpsertPartitionOption = false;
            } else if (resource.partitionOption === 'PartitionByKey') {
                result.enableUpsertPartitionOption = true;
            } else {
                ValueAdapter.toModel(result, LakehouseTableCopySink.UPSERT_PARTITION_OPTION, resource.partitionOption);
            }

            ValueAdapter.toModel(result, LakehouseTableCopySink.UPSERT_PARTITION_KEYS, resource.partitionNameList);
            if (Array.isArray(resource.partitionNameList) && !arrayIsNullOrEmpty(resource.partitionNameList)) {
                result.upsertPartitionNameList = [];
                (resource.partitionNameList as string[]).forEach((partitionName: string) => {
                    const modelPartitionColumn = Owned.create<StoreSettingPartitionKey>(
                        StoreSettingPartitionKey.MODEL,
                        result
                    );
                    modelPartitionColumn.partitionKey = partitionName;
                    result.upsertPartitionNameList.push(modelPartitionColumn);
                });
            }

            // set value for overwrite mode
            if (!isExpressionObject(resource.tableActionOption)) {
                result.enablePartitionOption = null;
                result.partitionNameList = null;
                result.enableAppendPartitionOption = null;
                result.appendPartitionNameList = null;
            }
        }
    }

    private _lhTableActionAppendToModel(resource: Model.LakehouseTableSink, result: LakehouseTableCopySink): void {
        if (resource.partitionOption == null) {
            result.enableAppendPartitionOption = null;
        } else if (resource.partitionOption === 'None') {
            result.enableAppendPartitionOption = false;
        } else if (resource.partitionOption === 'PartitionByKey') {
            result.enableAppendPartitionOption = true;
        } else {
            ValueAdapter.toModel(result, LakehouseTableCopySink.APPEND_PARTITION_OPTION, resource.partitionOption);
        }

        ValueAdapter.toModel(result, LakehouseTableCopySink.APPEND_PARTITION_KEYS, resource.partitionNameList);
        if (Array.isArray(resource.partitionNameList) && !arrayIsNullOrEmpty(resource.partitionNameList)) {
            result.appendPartitionNameList = [];
            (resource.partitionNameList as string[]).forEach((partitionName: string) => {
                const modelPartitionColumn = Owned.create<StoreSettingPartitionKey>(
                    StoreSettingPartitionKey.MODEL,
                    result
                );
                modelPartitionColumn.partitionKey = partitionName;
                result.appendPartitionNameList.push(modelPartitionColumn);
            });
        }

        // set value for overwrite mode
        if (!isExpressionObject(resource.tableActionOption)) {
            result.enablePartitionOption = null;
            result.partitionNameList = null;
            result.enableUpsertPartitionOption = null;
            result.upsertPartitionNameList = null;
        }
    }

    private _lhTableActionOverwriteToModel(resource: Model.LakehouseTableSink, result: LakehouseTableCopySink): void {
        if (resource.partitionOption == null || resource.partitionOption === 'None') {
            result.enablePartitionOption = false;
        } else if (resource.partitionOption === 'PartitionByKey') {
            result.enablePartitionOption = true;
        } else {
            ValueAdapter.toModel(result, LakehouseTableCopySink.PARTITION_OPTION, resource.partitionOption);
        }

        ValueAdapter.toModel(result, LakehouseTableCopySink.PARTITION_KEYS, resource.partitionNameList);
        if (Array.isArray(resource.partitionNameList) && !arrayIsNullOrEmpty(resource.partitionNameList)) {
            result.partitionNameList = [];
            (resource.partitionNameList as string[]).forEach((partitionName: string) => {
                const modelPartitionColumn = Owned.create<StoreSettingPartitionKey>(
                    StoreSettingPartitionKey.MODEL,
                    result
                );
                modelPartitionColumn.partitionKey = partitionName;
                result.partitionNameList.push(modelPartitionColumn);
            });
        }

        if (!isExpressionObject(resource.tableActionOption)) {
            result.enableAppendPartitionOption = null;
            result.appendPartitionNameList = null;
            result.enableUpsertPartitionOption = null;
            result.upsertPartitionNameList = null;
        }
    }
}
